import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { PageLoader, LoadingSpinner } from '../components/common/Loading';
import { AuthContext } from '../context/AuthContext';
import { WalletAPI, UserAPI } from '../services/api';
import { 
  FiArrowUp, 
  FiArrowDown, 
  FiCreditCard, 
  FiShield,
  FiClock,
  FiCheckCircle,
  FiXCircle,
  FiFilter,
  FiDownload,
  FiEye,
  FiEyeOff,
  FiPlus,
  FiMinus,
  FiRefreshCw,
  FiExternalLink
} from 'react-icons/fi';
import { FaBitcoin } from 'react-icons/fa';

const WalletPage = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [processing, setProcessing] = useState(false);
  
  // Real data from backend
  const [walletData, setWalletData] = useState({
    balance: 0,
    locked_balance: 0,
    available_balance: 0,
    bitcoin_address: null
  });
  
  const [transactions, setTransactions] = useState([]);
  const [filterType, setFilterType] = useState('all');
  const [timeRange, setTimeRange] = useState('7days');
  const [showBalance, setShowBalance] = useState(true);
  
  // Deposit/Withdraw forms
  const [depositAmount, setDepositAmount] = useState('');
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [withdrawAddress, setWithdrawAddress] = useState('');
  const [selectedMethod, setSelectedMethod] = useState('bitcoin');
  
  const quickAmounts = [10, 25, 50, 100, 250, 500];

  // Fetch wallet data
  const fetchWalletData = async () => {
    try {
      if (!refreshing) setLoading(true);
      
      // Get wallet balance
      const balanceResponse = await WalletAPI.getBalance();
      if (balanceResponse && balanceResponse.success !== false) {
        setWalletData({
          balance: balanceResponse.balance || 0,
          locked_balance: balanceResponse.locked_balance || 0,
          available_balance: balanceResponse.available_balance || 0,
          bitcoin_address: balanceResponse.bitcoin_address || null
        });
      }
      
      // Get transactions
      const transactionsResponse = await WalletAPI.getTransactions({ limit: 20 });
      if (transactionsResponse && transactionsResponse.success !== false) {
        setTransactions(transactionsResponse.transactions || []);
      }
      
    } catch (error) {
      console.error('Error fetching wallet data:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Initial fetch
  useEffect(() => {
    if (user) {
      fetchWalletData();
    } else {
      navigate('/login');
    }
  }, [user, navigate]);

  // Filter transactions
  const filteredTransactions = React.useMemo(() => {
    let filtered = transactions || [];
    
    if (filterType !== 'all') {
      filtered = filtered.filter(t => t.transaction_type === filterType);
    }
    
    if (timeRange !== 'all') {
      const days = parseInt(timeRange);
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - days);
      
      filtered = filtered.filter(t => new Date(t.created_at) > cutoffDate);
    }
    
    return filtered;
  }, [transactions, filterType, timeRange]);

  const handleDeposit = async () => {
    if (!depositAmount || parseFloat(depositAmount) <= 0) {
      alert('Please enter a valid deposit amount');
      return;
    }

    setProcessing(true);
    try {
      // Get or generate deposit address
      const addressResponse = await WalletAPI.getDepositAddress();
      if (addressResponse && addressResponse.success !== false) {
        alert(`Please send ${depositAmount} BTC to:\n\n${addressResponse.address}\n\nYour deposit will be credited after 3 confirmations.`);
        setDepositAmount('');
      } else {
        alert(addressResponse?.error || 'Failed to get deposit address');
      }
    } catch (error) {
      console.error('Deposit error:', error);
      alert('Failed to process deposit request');
    } finally {
      setProcessing(false);
    }
  };

  const handleWithdraw = async () => {
    if (!withdrawAmount || parseFloat(withdrawAmount) <= 0) {
      alert('Please enter a valid withdrawal amount');
      return;
    }

    if (!withdrawAddress) {
      alert('Please enter a Bitcoin address');
      return;
    }

    if (parseFloat(withdrawAmount) > walletData.available_balance) {
      alert('Insufficient available balance');
      return;
    }

    if (parseFloat(withdrawAmount) < 0.0001) {
      alert('Minimum withdrawal is 0.0001 BTC');
      return;
    }

    setProcessing(true);
    try {
      const response = await WalletAPI.requestWithdrawal(withdrawAmount, withdrawAddress);
      if (response && response.success !== false) {
        alert(`✅ Withdrawal request submitted!\n\nAmount: ${withdrawAmount} BTC\nTransaction ID: ${response.transaction_id}\n\nProcessing time: 1-3 hours`);
        setWithdrawAmount('');
        setWithdrawAddress('');
        fetchWalletData(); // Refresh data
      } else {
        alert(response?.error || 'Failed to process withdrawal');
      }
    } catch (error) {
      console.error('Withdrawal error:', error);
      alert(error?.message || 'Failed to process withdrawal request');
    } finally {
      setProcessing(false);
    }
  };

  const handleQuickAmount = (amount) => {
    if (activeTab === 'deposit') {
      setDepositAmount(amount.toString());
    } else if (activeTab === 'withdraw') {
      setWithdrawAmount(amount.toString());
    }
  };

  const formatAmount = (amount, currency = 'USD') => {
    const num = parseFloat(amount);
    if (currency === 'BTC') {
      return `${num.toFixed(8)} BTC`;
    }
    return `$${Math.abs(num).toFixed(2)}`;
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'completed': return 'text-green-400';
      case 'pending': return 'text-yellow-400';
      case 'processing': return 'text-blue-400';
      case 'failed': return 'text-red-400';
      default: return 'text-gray-400';
    }
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'completed': return <FiCheckCircle className="w-4 h-4 text-green-400" />;
      case 'pending': return <FiClock className="w-4 h-4 text-yellow-400" />;
      case 'failed': return <FiXCircle className="w-4 h-4 text-red-400" />;
      default: return null;
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return 'Today, ' + date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays === 1) {
      return 'Yesterday, ' + date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    } else {
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
  };

  const getTransactionTypeLabel = (type) => {
    switch(type) {
      case 'deposit': return 'Deposit';
      case 'withdrawal': return 'Withdrawal';
      case 'bet': return 'Bet';
      case 'win': return 'Win';
      case 'bonus': return 'Bonus';
      default: return type;
    }
  };

  const generateDepositAddress = async () => {
    setProcessing(true);
    try {
      const response = await WalletAPI.generateDepositAddress();
      if (response && response.success !== false) {
        alert('New Bitcoin address generated successfully!');
        fetchWalletData();
      } else {
        alert(response?.error || 'Failed to generate address');
      }
    } catch (error) {
      console.error('Generate address error:', error);
      alert('Failed to generate new address');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-deep-black pt-16">
        <Header />
        <PageLoader text="Loading wallet data..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-deep-black pt-16">
      <Header />
      
      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 py-4 sm:py-6">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 sm:mb-6 gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-white">💰 Wallet</h1>
            <p className="text-gray-400 text-sm">Manage your Bitcoin funds and transactions</p>
          </div>
          
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowBalance(!showBalance)}
              className="flex items-center gap-2 text-gray-400 hover:text-white"
            >
              {showBalance ? <FiEyeOff className="w-4 h-4" /> : <FiEye className="w-4 h-4" />}
              <span className="text-sm hidden sm:inline">{showBalance ? 'Hide' : 'Show'} Balance</span>
            </button>
            
            <button
              onClick={() => {
                setRefreshing(true);
                fetchWalletData();
              }}
              disabled={refreshing}
              className="flex items-center gap-2 text-gray-400 hover:text-white disabled:opacity-50"
            >
              <FiRefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              <span className="text-sm hidden sm:inline">Refresh</span>
            </button>
          </div>
        </div>

        {/* Balance Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 mb-6">
          {/* Available Balance */}
          <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-xl p-4 sm:p-6">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-600 rounded-lg">
                  <FiArrowDown className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-sm sm:text-base text-gray-300">Available Balance</h3>
                  <div className="text-lg sm:text-2xl font-bold text-white">
                    {showBalance ? formatAmount(walletData.available_balance, 'BTC') : '••••••••'}
                  </div>
                </div>
              </div>
            </div>
            <div className="text-xs sm:text-sm text-gray-400">
              Ready for betting and withdrawal
            </div>
          </div>

          {/* Locked Balance */}
          <div className="bg-gradient-to-br from-yellow-900/30 to-yellow-800/20 border border-yellow-700/30 rounded-xl p-4 sm:p-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 bg-yellow-600 rounded-lg">
                <FiClock className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
              </div>
              <div>
                <h3 className="text-sm sm:text-base text-gray-300">Locked Balance</h3>
                <div className="text-lg sm:text-2xl font-bold text-yellow-400">
                  {showBalance ? formatAmount(walletData.locked_balance, 'BTC') : '••••••••'}
                </div>
              </div>
            </div>
            <div className="text-xs sm:text-sm text-gray-400">
              In pending bets and withdrawals
            </div>
          </div>

          {/* Total Balance */}
          <div className="bg-gradient-to-br from-blue-900/30 to-blue-800/20 border border-blue-700/30 rounded-xl p-4 sm:p-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 bg-blue-600 rounded-lg">
                <FiShield className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
              </div>
              <div>
                <h3 className="text-sm sm:text-base text-gray-300">Total Balance</h3>
                <div className="text-lg sm:text-2xl font-bold text-blue-400">
                  {showBalance ? formatAmount(walletData.balance, 'BTC') : '••••••••'}
                </div>
              </div>
            </div>
            <div className="text-xs sm:text-sm text-gray-400">
              Combined available + locked
            </div>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-4">
          {/* Left Column - Main Content */}
          <div className="flex-1">
            {/* Tabs */}
            <div className="flex overflow-x-auto pb-2 mb-4 sm:mb-6">
              <div className="flex space-x-2 min-w-max">
                {['overview', 'deposit', 'withdraw', 'transactions'].map(tab => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`px-4 py-2 rounded-lg capitalize text-sm sm:text-base whitespace-nowrap ${
                      activeTab === tab
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                    }`}
                  >
                    {tab === 'overview' && '📊 Overview'}
                    {tab === 'deposit' && '💰 Deposit'}
                    {tab === 'withdraw' && '💸 Withdraw'}
                    {tab === 'transactions' && '📝 Transactions'}
                  </button>
                ))}
              </div>
            </div>

            {/* Tab Content */}
            <div className="bg-gray-900 rounded-xl p-4 sm:p-6">
              {/* Overview Tab */}
              {activeTab === 'overview' && (
                <div>
                  <h3 className="text-xl font-bold text-white mb-6">Wallet Overview</h3>
                  
                  {/* Bitcoin Address */}
                  <div className="mb-6 bg-gray-800 rounded-lg p-4">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
                      <div>
                        <h4 className="font-bold text-white mb-1 flex items-center gap-2">
                          <FaBitcoin className="w-5 h-5 text-yellow-500" />
                          Bitcoin Deposit Address
                        </h4>
                        <p className="text-gray-400 text-sm">
                          Send BTC to this address for deposits
                        </p>
                      </div>
                      <button
                        onClick={generateDepositAddress}
                        disabled={processing}
                        className="px-4 py-2 bg-gradient-to-r from-yellow-600 to-yellow-700 hover:from-yellow-500 hover:to-yellow-600 text-white rounded-lg text-sm font-medium transition-all disabled:opacity-50"
                      >
                        {processing ? <LoadingSpinner size="sm" color="white" /> : 'Generate New'}
                      </button>
                    </div>
                    
                    {walletData.bitcoin_address ? (
                      <div>
                        <div className="bg-black/50 rounded-lg p-3 mb-3">
                          <code className="text-yellow-400 text-sm break-all font-mono">
                            {walletData.bitcoin_address}
                          </code>
                        </div>
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(walletData.bitcoin_address);
                            alert('Address copied to clipboard!');
                          }}
                          className="w-full py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg text-sm transition-colors"
                        >
                          Copy Address
                        </button>
                      </div>
                    ) : (
                      <div className="text-center py-4">
                        <p className="text-gray-400">No deposit address generated yet</p>
                        <button
                          onClick={generateDepositAddress}
                          className="mt-2 px-4 py-2 bg-gradient-to-r from-yellow-600 to-yellow-700 hover:from-yellow-500 hover:to-yellow-600 text-white rounded-lg text-sm"
                        >
                          Generate Address
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Stats */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="bg-gray-800 rounded-lg p-4">
                      <h4 className="font-bold text-white mb-3">Deposit Info</h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-400">Min Deposit:</span>
                          <span className="text-white">0.0001 BTC</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-400">Confirmations:</span>
                          <span className="text-white">3 required</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-400">Network Fee:</span>
                          <span className="text-white">Paid by sender</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="bg-gray-800 rounded-lg p-4">
                      <h4 className="font-bold text-white mb-3">Withdrawal Info</h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-400">Min Withdrawal:</span>
                          <span className="text-white">0.0001 BTC</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-400">Withdrawal Fee:</span>
                          <span className="text-white">0.0005 BTC</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-400">Processing Time:</span>
                          <span className="text-white">1-3 hours</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Deposit Tab */}
              {activeTab === 'deposit' && (
                <div>
                  <h3 className="text-xl font-bold text-white mb-6">Deposit Bitcoin</h3>
                  
                  {/* Quick Amounts */}
                  <div className="mb-6">
                    <label className="block text-gray-300 mb-3">Quick Amounts (USD)</label>
                    <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                      {quickAmounts.map(amount => (
                        <button
                          key={amount}
                          onClick={() => handleQuickAmount(amount)}
                          className={`py-2 rounded-lg text-sm transition-colors ${
                            depositAmount === amount.toString()
                              ? 'bg-blue-600 text-white'
                              : 'bg-gray-800 hover:bg-gray-700 text-gray-300'
                          }`}
                        >
                          ${amount}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Amount Input */}
                  <div className="mb-6">
                    <label className="block text-gray-300 mb-2">Deposit Amount (USD)</label>
                    <div className="relative">
                      <div className="absolute left-4 top-1/2 transform -translate-y-1/2">
                        <FiArrowDown className="text-gray-400" />
                      </div>
                      <input
                        type="number"
                        value={depositAmount}
                        onChange={(e) => setDepositAmount(e.target.value)}
                        placeholder="0.00"
                        className="w-full pl-12 pr-20 py-3 sm:py-4 bg-gray-800 text-white rounded-lg border border-gray-700 focus:border-blue-500 focus:outline-none text-lg"
                      />
                      <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex items-center gap-1">
                        <button
                          onClick={() => setDepositAmount(Math.max(0, parseFloat(depositAmount || 0) - 10).toString())}
                          className="p-2 bg-gray-700 hover:bg-gray-600 rounded"
                        >
                          <FiMinus className="w-4 h-4 text-white" />
                        </button>
                        <button
                          onClick={() => setDepositAmount((parseFloat(depositAmount || 0) + 10).toString())}
                          className="p-2 bg-gray-700 hover:bg-gray-600 rounded"
                        >
                          <FiPlus className="w-4 h-4 text-white" />
                        </button>
                      </div>
                    </div>
                    <div className="text-gray-400 text-xs mt-2">
                      Minimum: $10 • Maximum: No limit
                    </div>
                  </div>

                  {/* Deposit Address */}
                  <div className="mb-6">
                    <label className="block text-gray-300 mb-2">Your Bitcoin Address</label>
                    {walletData.bitcoin_address ? (
                      <>
                        <div className="bg-black/50 rounded-lg p-3 mb-3">
                          <code className="text-yellow-400 text-sm break-all font-mono">
                            {walletData.bitcoin_address}
                          </code>
                        </div>
                        <p className="text-gray-400 text-sm mb-3">
                          Send BTC to this address. Deposit will be credited after 3 confirmations.
                        </p>
                        <div className="flex gap-2">
                          <button
                            onClick={() => {
                              navigator.clipboard.writeText(walletData.bitcoin_address);
                              alert('Address copied!');
                            }}
                            className="flex-1 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg text-sm"
                          >
                            Copy Address
                          </button>
                          <button
                            onClick={() => window.open(`https://blockchair.com/bitcoin/address/${walletData.bitcoin_address}`, '_blank')}
                            className="flex-1 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm flex items-center justify-center gap-2"
                          >
                            <FiExternalLink className="w-4 h-4" />
                            View on Explorer
                          </button>
                        </div>
                      </>
                    ) : (
                      <div className="text-center py-4 border-2 border-dashed border-gray-700 rounded-lg">
                        <p className="text-gray-400 mb-3">No deposit address generated</p>
                        <button
                          onClick={generateDepositAddress}
                          className="px-4 py-2 bg-gradient-to-r from-yellow-600 to-yellow-700 hover:from-yellow-500 hover:to-yellow-600 text-white rounded-lg"
                        >
                          Generate Address
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Deposit Button */}
                  <button
                    onClick={handleDeposit}
                    disabled={!depositAmount || processing}
                    className={`w-full py-4 rounded-lg font-bold text-lg transition-all flex items-center justify-center gap-2 ${
                      !depositAmount || processing
                        ? 'bg-gray-700 text-gray-400 cursor-not-allowed'
                        : 'bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 text-white shadow-lg hover:shadow-blue-500/30'
                    }`}
                  >
                    {processing ? (
                      <>
                        <LoadingSpinner size="sm" color="white" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <FiArrowDown className="w-5 h-5" />
                        Get Deposit Address
                      </>
                    )}
                  </button>
                </div>
              )}

              {/* Withdraw Tab */}
              {activeTab === 'withdraw' && (
                <div>
                  <h3 className="text-xl font-bold text-white mb-6">Withdraw Bitcoin</h3>
                  
                  {/* Quick Amounts */}
                  <div className="mb-6">
                    <label className="block text-gray-300 mb-3">Quick Amounts (BTC)</label>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {[0.001, 0.01, 0.1, 0.5, 1, 5].map(amount => (
                        <button
                          key={amount}
                          onClick={() => setWithdrawAmount(amount.toString())}
                          className={`py-2 rounded-lg text-sm transition-colors ${
                            withdrawAmount === amount.toString()
                              ? 'bg-green-600 text-white'
                              : 'bg-gray-800 hover:bg-gray-700 text-gray-300'
                          }`}
                        >
                          {amount} BTC
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Amount Input */}
                  <div className="mb-6">
                    <label className="block text-gray-300 mb-2">Withdrawal Amount (BTC)</label>
                    <div className="relative">
                      <div className="absolute left-4 top-1/2 transform -translate-y-1/2">
                        <FiArrowUp className="text-gray-400" />
                      </div>
                      <input
                        type="number"
                        step="0.00000001"
                        value={withdrawAmount}
                        onChange={(e) => setWithdrawAmount(e.target.value)}
                        placeholder="0.00000000"
                        className="w-full pl-12 pr-32 py-3 sm:py-4 bg-gray-800 text-white rounded-lg border border-gray-700 focus:border-green-500 focus:outline-none text-lg"
                      />
                      <div className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm">
                        Max: {walletData.available_balance.toFixed(8)} BTC
                      </div>
                    </div>
                    <div className="flex justify-between text-gray-400 text-xs mt-2">
                      <span>Available: {walletData.available_balance.toFixed(8)} BTC</span>
                      <button
                        onClick={() => setWithdrawAmount(walletData.available_balance.toString())}
                        className="text-blue-400 hover:text-blue-300"
                      >
                        Withdraw All
                      </button>
                    </div>
                  </div>

                  {/* Bitcoin Address */}
                  <div className="mb-6">
                    <label className="block text-gray-300 mb-2">Bitcoin Address *</label>
                    <input
                      type="text"
                      value={withdrawAddress}
                      onChange={(e) => setWithdrawAddress(e.target.value)}
                      placeholder="1A1zP1eP5QGefi2DMPTfTL5SLmv7DivfNa"
                      className="w-full px-4 py-3 bg-gray-800 text-white rounded-lg border border-gray-700 focus:border-green-500 focus:outline-none font-mono"
                    />
                    <div className="text-gray-400 text-xs mt-2">
                      Enter a valid Bitcoin address. Double-check before submitting.
                    </div>
                  </div>

                  {/* Withdrawal Details */}
                  <div className="bg-gray-800 rounded-lg p-4 mb-6">
                    <h4 className="font-bold text-white mb-3">Withdrawal Summary</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-400">Amount:</span>
                        <span className="text-white">{withdrawAmount || '0.00000000'} BTC</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">Network Fee:</span>
                        <span className="text-white">0.0005 BTC</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">You Receive:</span>
                        <span className="text-green-400">
                          {withdrawAmount ? (parseFloat(withdrawAmount) - 0.0005).toFixed(8) : '0.00000000'} BTC
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Withdraw Button */}
                  <button
                    onClick={handleWithdraw}
                    disabled={!withdrawAmount || !withdrawAddress || parseFloat(withdrawAmount) < 0.0001 || processing}
                    className={`w-full py-4 rounded-lg font-bold text-lg transition-all flex items-center justify-center gap-2 ${
                      !withdrawAmount || !withdrawAddress || parseFloat(withdrawAmount) < 0.0001 || processing
                        ? 'bg-gray-700 text-gray-400 cursor-not-allowed'
                        : 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 text-white shadow-lg hover:shadow-green-500/30'
                    }`}
                  >
                    {processing ? (
                      <>
                        <LoadingSpinner size="sm" color="white" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <FiArrowUp className="w-5 h-5" />
                        Withdraw Bitcoin
                      </>
                    )}
                  </button>
                </div>
              )}

              {/* Transactions Tab */}
              {activeTab === 'transactions' && (
                <div>
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-3">
                    <h3 className="text-xl font-bold text-white">Transaction History</h3>
                    
                    {/* Filters */}
                    <div className="flex flex-wrap gap-2">
                      <select
                        value={filterType}
                        onChange={(e) => setFilterType(e.target.value)}
                        className="bg-gray-800 text-white px-3 py-2 rounded-lg text-sm"
                      >
                        <option value="all">All Types</option>
                        <option value="deposit">Deposits</option>
                        <option value="withdrawal">Withdrawals</option>
                        <option value="bet">Bets</option>
                        <option value="win">Wins</option>
                        <option value="bonus">Bonuses</option>
                      </select>
                      
                      <select
                        value={timeRange}
                        onChange={(e) => setTimeRange(e.target.value)}
                        className="bg-gray-800 text-white px-3 py-2 rounded-lg text-sm"
                      >
                        <option value="7days">Last 7 Days</option>
                        <option value="30days">Last 30 Days</option>
                        <option value="90days">Last 90 Days</option>
                        <option value="all">All Time</option>
                      </select>
                    </div>
                  </div>

                  {filteredTransactions.length === 0 ? (
                    <div className="text-center py-8">
                      <div className="text-4xl mb-4">📊</div>
                      <h4 className="text-lg font-bold text-white mb-2">No transactions found</h4>
                      <p className="text-gray-400">Try adjusting your filters or make your first transaction</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {filteredTransactions.map(transaction => (
                        <div key={transaction.id} className="bg-gray-800 rounded-lg p-4 hover:bg-gray-750 transition-colors">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="text-2xl">
                                {transaction.transaction_type === 'deposit' ? '💰' :
                                 transaction.transaction_type === 'withdrawal' ? '💸' :
                                 transaction.transaction_type === 'bet' ? '⚽' :
                                 transaction.transaction_type === 'win' ? '🎉' : '🎁'}
                              </div>
                              <div>
                                <div className="font-bold text-white text-sm sm:text-base">
                                  {getTransactionTypeLabel(transaction.transaction_type)}
                                  {transaction.description && ` - ${transaction.description}`}
                                </div>
                                <div className="text-gray-400 text-xs sm:text-sm">
                                  {formatDate(transaction.created_at)}
                                  {transaction.bitcoin_address && ` • ${transaction.bitcoin_address.substring(0, 12)}...`}
                                </div>
                              </div>
                            </div>
                            <div className="text-right">
                              <div className={`font-bold text-lg ${
                                parseFloat(transaction.amount) > 0 ? 'text-green-400' : 'text-red-400'
                              }`}>
                                {parseFloat(transaction.amount) > 0 ? '+' : ''}{formatAmount(transaction.amount, 'BTC')}
                              </div>
                              <div className="flex items-center justify-end gap-1 mt-1">
                                {getStatusIcon(transaction.status)}
                                <span className={`text-xs ${getStatusColor(transaction.status)}`}>
                                  {transaction.status}
                                </span>
                              </div>
                              {transaction.fee && transaction.fee > 0 && (
                                <div className="text-gray-400 text-xs mt-1">
                                  Fee: {transaction.fee} BTC
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Right Column - Stats & Info */}
          <div className="lg:w-80">
            {/* Stats Card */}
            <div className="bg-gray-900 rounded-xl p-4 sm:p-6 mb-4">
              <h3 className="text-lg font-bold text-white mb-4">📈 Statistics</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-gray-300 text-sm">Total Deposits</span>
                  <span className="text-white font-medium">
                    {transactions
                      .filter(t => t.transaction_type === 'deposit' && t.status === 'completed')
                      .reduce((sum, t) => sum + parseFloat(t.amount), 0)
                      .toFixed(8)} BTC
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-300 text-sm">Total Withdrawals</span>
                  <span className="text-white font-medium">
                    {Math.abs(transactions
                      .filter(t => t.transaction_type === 'withdrawal' && t.status === 'completed')
                      .reduce((sum, t) => sum + parseFloat(t.amount), 0))
                      .toFixed(8)} BTC
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-300 text-sm">Transaction Count</span>
                  <span className="text-white font-medium">{transactions.length}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-300 text-sm">Success Rate</span>
                  <span className="text-green-400 font-medium">
                    {transactions.length > 0 
                      ? Math.round((transactions.filter(t => t.status === 'completed').length / transactions.length) * 100)
                      : 0}%
                  </span>
                </div>
              </div>
            </div>

            {/* Security Card */}
            <div className="bg-gray-900 rounded-xl p-4 sm:p-6 mb-4">
              <h3 className="text-lg font-bold text-white mb-4">🛡️ Security Tips</h3>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <FiShield className="w-5 h-5 text-green-400 mt-0.5" />
                  <div>
                    <p className="text-white text-sm font-medium">Verify Addresses</p>
                    <p className="text-gray-400 text-xs">Always double-check Bitcoin addresses before sending</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <FiClock className="w-5 h-5 text-yellow-400 mt-0.5" />
                  <div>
                    <p className="text-white text-sm font-medium">Wait for Confirmations</p>
                    <p className="text-gray-400 text-xs">Deposits require 3 confirmations (≈30 mins)</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <FiShield className="w-5 h-5 text-blue-400 mt-0.5" />
                  <div>
                    <p className="text-white text-sm font-medium">Use Whitelist</p>
                    <p className="text-gray-400 text-xs">Add trusted addresses to withdrawal whitelist</p>
                  </div>
                </div>
              </div>
              <button
                onClick={() => navigate('/profile')}
                className="w-full mt-4 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg text-sm transition-colors"
              >
                Manage Security Settings
              </button>
            </div>

            {/* Quick Links */}
            <div className="bg-gray-900 rounded-xl p-4 sm:p-6">
              <h3 className="text-lg font-bold text-white mb-4">🔗 Quick Links</h3>
              <div className="space-y-2">
                <button
                  onClick={() => navigate('/profile')}
                  className="w-full text-left p-3 bg-gray-800 hover:bg-gray-700 rounded-lg text-sm text-gray-300 transition-colors"
                >
                  👤 Profile Settings
                </button>
                <button
                  onClick={() => navigate('/kyc')}
                  className="w-full text-left p-3 bg-gray-800 hover:bg-gray-700 rounded-lg text-sm text-gray-300 transition-colors"
                >
                  📋 KYC Verification
                </button>
                <button
                  onClick={() => navigate('/support')}
                  className="w-full text-left p-3 bg-gray-800 hover:bg-gray-700 rounded-lg text-sm text-gray-300 transition-colors"
                >
                  📞 Contact Support
                </button>
                <button
                  onClick={() => window.open('https://www.blockchain.com/explorer', '_blank')}
                  className="w-full text-left p-3 bg-gray-800 hover:bg-gray-700 rounded-lg text-sm text-gray-300 transition-colors flex items-center justify-between"
                >
                  <span>🔍 Blockchain Explorer</span>
                  <FiExternalLink className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Bottom Navigation */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-gray-900 border-t border-gray-800 p-3 z-30">
        <div className="flex justify-around">
          <button
            onClick={() => setActiveTab('deposit')}
            className={`flex flex-col items-center ${activeTab === 'deposit' ? 'text-blue-400' : 'text-gray-400'} hover:text-white`}
          >
            <FiArrowDown className="w-5 h-5 mb-1" />
            <span className="text-xs">Deposit</span>
          </button>
          <button
            onClick={() => setActiveTab('withdraw')}
            className={`flex flex-col items-center ${activeTab === 'withdraw' ? 'text-green-400' : 'text-gray-400'} hover:text-white`}
          >
            <FiArrowUp className="w-5 h-5 mb-1" />
            <span className="text-xs">Withdraw</span>
          </button>
          <button
            onClick={() => setActiveTab('transactions')}
            className={`flex flex-col items-center ${activeTab === 'transactions' ? 'text-yellow-400' : 'text-gray-400'} hover:text-white`}
          >
            <FiCreditCard className="w-5 h-5 mb-1" />
            <span className="text-xs">History</span>
          </button>
          <button
            onClick={() => navigate('/profile')}
            className="flex flex-col items-center text-gray-400 hover:text-white"
          >
            <FiShield className="w-5 h-5 mb-1" />
            <span className="text-xs">Profile</span>
          </button>
        </div>
      </div>

      <div className="pb-20 lg:pb-0" />
      <Footer />
    </div>
  );
};

export default WalletPage;